import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  VENDOR_FIELDS,
  VENDOR_LABELS,
  type VendorKey,
  vendorPatchBody,
  vendorToggleBody,
} from '../../../features/admin/vendors'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { ApiError } from '../../../lib/api-client'
import { Btn, Chip, Inline, inputCls } from './controls'
import { Bar, mono, Note, Panel, SkelPanels, SLink } from './ui'

/** Error text for admin mutations; a 409 gets an explicit conflict prefix. */
export function adminError(e: unknown): string {
  const msg = apiErrorMessage(e)
  return e instanceof ApiError && e.status === 409 ? `Conflict: ${msg}` : msg
}

export function AdminCrumbs({ page, parent }: { page: string; parent?: { label: string; to: string } }) {
  const hover = 'hover:text-[var(--color-fg)]'
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-[12px] text-[var(--color-muted)]"
      style={mono}
    >
      <span>admin</span>
      <span aria-hidden="true">/</span>
      {parent ? (
        <>
          <SLink to={parent.to} className={hover}>
            {parent.label}
          </SLink>
          <span aria-hidden="true">/</span>
        </>
      ) : null}
      <span className="text-[var(--color-fg)]">{page}</span>
    </nav>
  )
}

export function AdminPage({
  page,
  title,
  parent,
  meta,
  right,
  loading,
  error,
  children,
}: {
  page: string
  title: string
  parent?: { label: string; to: string }
  meta?: ReactNode
  right?: ReactNode
  loading?: boolean
  error?: boolean
  children: ReactNode
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Bar crumbs={<AdminCrumbs page={page} parent={parent} />} title={title} meta={meta} right={right} />
      {loading ? (
        <SkelPanels n={2} />
      ) : error ? (
        <Note error>Could not load {title.toLowerCase()}. You may lack access; retry shortly.</Note>
      ) : (
        <div className="grid content-start gap-3 overflow-auto p-4">{children}</div>
      )}
    </div>
  )
}

export function Meta({ children }: { children: ReactNode }) {
  return (
    <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
      {children}
    </span>
  )
}

/** Masked secret with Reveal; revealed value auto-masks after 30s. Value lives only in component state. */
function SecretValue({ masked, reveal }: { masked: string; reveal: () => Promise<string> }) {
  const [value, setValue] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    setValue(null)
  }
  const show = async () => {
    setBusy(true)
    setErr(null)
    try {
      setValue(await reveal())
      timer.current = setTimeout(() => setValue(null), 30_000)
    } catch (e) {
      setErr(adminError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <div className="flex h-7 items-center gap-2">
        <code className="min-w-0 flex-1 truncate text-[12px]" style={mono}>
          {value ?? (masked || 'not set')}
        </code>
        {masked ? (
          value ? (
            <Btn onClick={hide}>Hide</Btn>
          ) : (
            <Btn onClick={show} disabled={busy}>
              {busy ? 'Revealing...' : 'Reveal'}
            </Btn>
          )
        ) : null}
      </div>
      {value ? <p className="text-[11px] text-[var(--color-muted)]">auto-masks in 30s</p> : null}
      {err ? <Inline tone="bad">{err}</Inline> : null}
    </div>
  )
}

const lbl = 'mb-1 block text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]'

/** One vendor panel: view, edit (blank secret = keep), enable toggle, reveal. */
export function VendorPanel({
  vendor,
  block,
  pending,
  error,
  onSave,
  reveal,
}: {
  vendor: VendorKey
  block: Record<string, unknown> | null
  pending: boolean
  error: unknown
  onSave: (body: { vendor: VendorKey; data: Record<string, unknown> }) => void
  reveal: (vendor: VendorKey, field: string) => Promise<string>
}) {
  const [edits, setEdits] = useState<Record<string, string>>({})
  const f = VENDOR_FIELDS[vendor]
  const enabled = block?.enabled === true
  const str = (k: string) => (typeof block?.[k] === 'string' ? (block[k] as string) : '')
  const patch = vendorPatchBody(vendor, edits)
  const dirty = Object.keys(patch.data).length > 0
  return (
    <Panel
      title={VENDOR_LABELS[vendor]}
      aside={
        <span className="flex items-center gap-2">
          <Chip tone={enabled ? 'ok' : 'off'}>{enabled ? 'enabled' : block ? 'disabled' : 'unset'}</Chip>
          <Btn disabled={pending} onClick={() => onSave(vendorToggleBody(vendor, !enabled))}>
            {enabled ? 'Disable' : 'Enable'}
          </Btn>
        </span>
      }
    >
      <div className="grid gap-x-4 gap-y-3 p-3 sm:grid-cols-2">
        {f.nonSecret.map((k) => (
          <label key={k} className="block">
            <span className={lbl}>{k}</span>
            <input
              className={inputCls}
              style={mono}
              defaultValue={str(k)}
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </label>
        ))}
        {f.secret.map((k) => (
          <div key={k}>
            <span className={lbl}>{k}</span>
            <SecretValue masked={str(k)} reveal={() => reveal(vendor, k)} />
            <input
              aria-label={`New ${k}`}
              className={`${inputCls} mt-1`}
              type="password"
              autoComplete="off"
              placeholder="blank = keep current"
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <div className="flex h-9 items-center justify-end gap-3 border-t border-[var(--color-border)] px-3">
        {error ? <Inline tone="bad">{adminError(error)}</Inline> : null}
        <Btn variant="primary" disabled={!dirty || pending} onClick={() => onSave(patch)}>
          {pending ? 'Saving...' : 'Save'}
        </Btn>
      </div>
    </Panel>
  )
}

export const rowCls = 'hover:bg-[var(--color-surface-2)]'
